import { useRef, useState, useEffect } from "react";
import { Upload, Image as ImageIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isEvtxFileName } from "@spectree/shared";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/useToast";
import { useUploadScreenshot } from "@/hooks/queries/use-fogbugz";

interface ScreenshotPasteProps {
  sessionId: string;
  /** Website: called with the server's description of the uploaded screenshot. */
  onUploadSuccess?: (description: string) => void;
  /**
   * Toolbox FogBugz Formulator (epic PI-SPEC-SW-E309): where a pasted or
   * picked image goes instead of the website upload route, for example the
   * Toolbox's private session folder (`fogbugzFormulator.addImage`). Rejects
   * with a plain message to show.
   */
  onFile?: (file: File) => Promise<void>;
}

/**
 * Paste (⌘V on a Mac, Ctrl+V elsewhere), drop or pick a screenshot. On the website it uploads to the
 * interview session (`POST /fogbugz/interview/:id/screenshot`); with `onFile`
 * (the Toolbox FogBugz Formulator) the file goes there instead and nothing is
 * sent to SpecTree until submit.
 */
export function ScreenshotPaste({ sessionId, onUploadSuccess, onFile }: ScreenshotPasteProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadScreenshot();
  const { toast } = useToast();
  // The state of an `onFile` hand-off (the website path uses the mutation's own state).
  const [localState, setLocalState] = useState<"idle" | "pending" | "success" | "error">("idle");
  const isPending = onFile ? localState === "pending" : uploadMutation.isPending;
  const isSuccess = onFile ? localState === "success" : uploadMutation.isSuccess;
  const isError = onFile ? localState === "error" : uploadMutation.isError;

  // Keep a ref to the latest handleFile so the global paste listener (mounted
  // once below) always invokes the freshest closure — otherwise stale
  // `onUploadSuccess` / `uploadMutation` references could route uploads to a
  // previous interview state after re-renders.
  const handleFileRef = useRef<(file: File) => void>(() => {});

  // Handle paste events globally
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            handleFileRef.current(file);
          }
          break;
        }
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [sessionId]);

  const validateFile = (file: File): boolean => {
    // Event logs have their own uploader; point the reporter to it rather
    // than showing the generic image error.
    if (isEvtxFileName(file.name)) {
      toast("Event logs go in the Event logs (.evtx) uploader.");
      return false;
    }

    // Validate type
    if (!file.type.startsWith("image/")) {
      toast("Only image files are supported");
      return false;
    }

    // Validate size (10 MB max)
    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast(
        `File too large. Maximum size is 10 MB (uploaded: ${(file.size / 1024 / 1024).toFixed(
          2
        )} MB)`
      );
      return false;
    }

    return true;
  };

  const handleFile = (file: File) => {
    if (!validateFile(file)) return;

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
      setUploadedFile(file);
    };
    reader.readAsDataURL(file);

    if (onFile) {
      setLocalState("pending");
      onFile(file)
        .then(() => {
          setLocalState("success");
        })
        .catch((error: unknown) => {
          setLocalState("error");
          toast(error instanceof Error ? error.message : "The screenshot could not be added");
          clearPreview();
        });
      return;
    }

    // Upload immediately
    uploadMutation.mutate(
      { sessionId, file },
      {
        onSuccess: (response) => {
          onUploadSuccess?.(response.data.description);
        },
        onError: (error) => {
          console.error("Screenshot upload failed:", error);
          toast(error instanceof Error ? error.message : "Upload failed");
          clearPreview();
        },
      }
    );
  };

  // Refresh the ref on every render so the paste listener sees the latest deps.
  handleFileRef.current = handleFile;

  const clearPreview = () => {
    setPreview(null);
    setUploadedFile(null);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone itself
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (file) {
      handleFile(file);
    }
  };

  return (
    <div className="space-y-2">
      {/* Drop zone — one line, so the interview's message list keeps its height */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={cn(
          "border-2 border-dashed rounded-lg px-4 py-3 text-center transition-colors",
          isDragging
            ? "border-primary bg-primary/5"
            : "border-white/[0.08] hover:border-white/[0.2]"
        )}
      >
        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
          <Upload className="h-4 w-4 flex-shrink-0" />
          <span>
            <strong className="text-foreground">Drop an image</strong> or{" "}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-primary hover:underline"
            >
              browse
            </button>
          </span>
          <span className="text-xs">
            or press <kbd className="px-1 py-0.5 bg-white/[0.08] rounded">{pasteShortcutLabel()}</kbd> to paste
          </span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileInput}
        />
      </div>

      {/* Preview */}
      {preview && (
        <div className="relative border border-white/[0.08] rounded-lg p-2">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <img
                src={preview}
                alt="Screenshot preview"
                className="h-12 w-12 object-cover rounded border border-white/[0.08]"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium truncate">
                    {uploadedFile?.name || "Screenshot"}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearPreview}
                  disabled={isPending}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {isPending ? (
                  <span className="text-primary">{onFile ? "Adding..." : "Uploading and analyzing..."}</span>
                ) : isSuccess ? (
                  <span className="text-green-500">{onFile ? "✓ Added" : "✓ Uploaded successfully"}</span>
                ) : isError ? (
                  <span className="text-red-500">✗ Upload failed</span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** The platform's paste shortcut label: ⌘V on a Mac, Ctrl+V elsewhere. */
export function pasteShortcutLabel(platform?: string): string {
  const name = platform ?? (typeof navigator === "undefined" ? "" : navigator.userAgent);
  return /mac|darwin|iphone|ipad/i.test(name) ? "⌘V" : "Ctrl+V";
}
